'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { IconButton } from '@/app/components/atoms/IconButton'
import { IconChevLeft, IconChevRight, IconClose, IconPlay } from '@/app/components/atoms/Icons'

import { FOCUSABLE_SELECTOR } from './PopUpSection.constants'
import styles from './PopUpSection.module.scss'
import type { PopUpSectionProps } from './PopUpSection.types'

/**
 * PopUpSection — pop-up de galería (imágenes + vídeos), reutilizable por
 * cualquier módulo que necesite recorrer una lista mixta a pantalla
 * ampliada. Réplica fiel (ronda 3) de `MultimediaLightbox` de
 * `eci-firefly-uikit`: cabecera con título + cerrar, carrusel de slides a
 * pantalla completa (scroll-snap nativo, sin bucle) con flechas + contador
 * en el pie, cada imagen a su ratio real (sin recortar ni deformar).
 *
 * Mismo contrato de accesibilidad modal ya usado por `BottomSheet`/
 * `SideSheet`: `role="dialog"` + `aria-modal="true"`, portal a
 * `document.body`, foco entra al abrir y vuelve al disparador al cerrar,
 * cierre con Escape/clic en el scrim, `Tab` cíclico. El algoritmo de
 * posición (offsetLeft/scrollLeft reales, no un ancho medio asumido) es el
 * mismo ya usado por `MultimediaGallery`/`PDSCarousel`.
 */
export function PopUpSection({ items, initialIndex, onClose, ariaLabel }: PopUpSectionProps) {
  const total = items.length
  const panelRef = useRef<HTMLDivElement | null>(null)
  const trackRef = useRef<HTMLUListElement | null>(null)
  const triggerRef = useRef<HTMLElement | null>(null)

  const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), total - 1))
  const [canPrev, setCanPrev] = useState(initialIndex > 0)
  const [canNext, setCanNext] = useState(initialIndex < total - 1)
  const [playingIds, setPlayingIds] = useState<ReadonlySet<string>>(new Set())

  const play = useCallback((id: string) => setPlayingIds((prev) => new Set(prev).add(id)), [])

  const sync = useCallback(() => {
    const track = trackRef.current
    if (!track || track.children.length === 0) return
    const children = Array.from(track.children) as HTMLElement[]
    let idx = 0
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      if (child && child.offsetLeft <= track.scrollLeft + 1) idx = i
    }
    const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth)
    setIndex(idx)
    setCanPrev(track.scrollLeft > 1)
    setCanNext(track.scrollLeft < maxScrollLeft - 1)
  }, [])

  const goTo = useCallback((targetIndex: number, smooth: boolean) => {
    const track = trackRef.current
    const target = track?.children[targetIndex] as HTMLElement | undefined
    if (!track || !target) return
    track.scrollTo({ left: target.offsetLeft, behavior: smooth ? 'smooth' : 'auto' })
  }, [])

  const goPrev = useCallback(() => goTo(Math.max(0, index - 1), true), [goTo, index])
  const goNext = useCallback(() => goTo(Math.min(total - 1, index + 1), true), [goTo, index, total])

  // Posición inicial sin animación — el `<ul>` ya existe en el primer
  // render (el portal aquí no gatea el montaje, a diferencia de la
  // referencia, que espera un 2º render tras `setMounted(true)`).
  useEffect(() => {
    goTo(initialIndex, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Bloqueo de scroll del body mientras está abierto.
  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  // Gestión del foco: entrada al abrir, restauración al cerrar.
  useEffect(() => {
    triggerRef.current = document.activeElement as HTMLElement | null

    const panel = panelRef.current
    if (panel) {
      const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      if (focusables.length > 0) {
        focusables[0]?.focus()
      } else {
        panel.focus()
      }
    }

    return () => {
      triggerRef.current?.focus?.()
    }
  }, [])

  // Escape + focus trap (Tab cíclico) + flechas de teclado para navegar.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key === 'ArrowLeft') {
        goPrev()
        return
      }
      if (event.key === 'ArrowRight') {
        goNext()
        return
      }
      if (event.key !== 'Tab') return

      const panel = panelRef.current
      if (!panel) return
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      if (focusables.length === 0) {
        event.preventDefault()
        panel.focus()
        return
      }
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (event.shiftKey && active === first) {
        event.preventDefault()
        last?.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, goPrev, goNext])

  if (typeof document === 'undefined') return null
  if (total === 0) return null

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <h2 className={styles.headerTitle}>{ariaLabel}</h2>
          <IconButton
            icon={<IconClose size={18} />}
            ariaLabel="Cerrar galería"
            variant="ghost"
            size="s"
            onClick={onClose}
          />
        </div>

        <div className={styles.body}>
          <ul ref={trackRef} className={styles.track} onScroll={sync} aria-label={ariaLabel}>
            {items.map((item) => (
              <li key={item.id} className={styles.slide}>
                {item.kind === 'video' ? (
                  playingIds.has(item.id) ? (
                    <iframe
                      className={styles.iframe}
                      src={`https://www.youtube-nocookie.com/embed/${item.youtubeId}?rel=0&modestbranding=1&autoplay=1`}
                      title={item.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.playThumb}
                      aria-label={`Reproducir: ${item.title}`}
                      onClick={() => play(item.id)}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element -- ratio real desconocido en build time (miniatura remota de YouTube), ver `.mediaImg` */}
                      <img
                        src={`https://img.youtube.com/vi/${item.youtubeId}/hqdefault.jpg`}
                        alt=""
                        className={styles.mediaImg}
                      />
                      <span className={styles.playIcon} aria-hidden="true">
                        <IconPlay size={24} />
                      </span>
                    </button>
                  )
                ) : (
                  // eslint-disable-next-line @next/next/no-img-element -- ratio real desconocido en build time (imagen remota del CMS), ver `.mediaImg`
                  <img src={item.src} alt={item.alt} className={styles.mediaImg} />
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.controls}>
          <IconButton
            icon={<IconChevLeft size={20} />}
            ariaLabel="Anterior"
            variant="ghost"
            size="s"
            onClick={goPrev}
            disabled={!canPrev}
          />
          <span className={styles.counter}>
            {index + 1} / {total}
          </span>
          <IconButton
            icon={<IconChevRight size={20} />}
            ariaLabel="Siguiente"
            variant="ghost"
            size="s"
            onClick={goNext}
            disabled={!canNext}
          />
        </div>
      </div>
    </div>,
    document.body,
  )
}
